import ky, { HTTPError } from "ky";
import { DraftLoadData, MapData } from "@/shared/types/types";
import { parseMap } from "@/shared/utils/map";
import { MapSchema } from "../shared/types/zod";
import { addBreadcrumb, captureException } from "@/sentry/Sentry";
import { convertColorsToLegs } from "@/utils/legs";

export type ApiResponse<T = any> = {
	success: boolean,
	data: T,
	error: string,
}

export async function getErrorMessage(error: unknown) {
	if (error instanceof HTTPError) {
		try {
			const res = await error.response.json();
			return res.message ?? undefined
		} catch (e) {
			return undefined
		}
	}
}

export function errorResponse(message: string): ApiResponse {
	return {
		success: false,
		data: {},
		error: message,
	}
}

export function successResponse(data: any = {}): ApiResponse {
	return {
		success: true,
		data,
		error: "",
	}
}

export async function initFirebaseAuth() {
	const { setupFirebaseAuth } = await import("./firebase");
	return setupFirebaseAuth();
}

export async function loadDraftMap(mapId: string): Promise<ApiResponse<DraftLoadData | undefined>> {
	return await (await import("./firebase")).loadDraftMap(mapId);
}

export async function loadMap(mapId: string, draft = false): Promise<ApiResponse<MapData | undefined>> {
	if (draft) {
		const loaded = await loadDraftMap(mapId);
		if (!loaded.success || !loaded.data?.map) {
			return errorResponse(loaded.error || "not_found");
		}
		return successResponse(loaded.data.map);
	}

	const MAX_RETRIES = 3;
	const RETRY_DELAY_MS = 1000;

	for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
		try {
			const response = await ky.get(`/api/map/${encodeURIComponent(mapId)}`, {
				timeout: 30000,
				retry: 0,
			}).json<{ map?: object }>();

			if (!response.map) {
				return errorResponse("not_found");
			}

			const map = parseMap(response.map, mapId);
			convertColorsToLegs(map);
			const parsed = MapSchema.parse(map);
			if (attempt > 1) {
				addBreadcrumb(`Loaded map at attempt ${attempt}`, { attempt });
			}
			return successResponse(parsed);
		} catch (error) {
			if (error instanceof HTTPError && error.response.status === 404) {
				return errorResponse("not_found");
			}

			if (attempt === MAX_RETRIES) {
				captureException(error as Error);
				return errorResponse("");
			}
			await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
		}
	}
	return errorResponse("");
}

export async function generateGpx(path: string) {
	return await (await import("./firebase")).generateGpx(path);
}